import { Formik, Form, useFormikContext, useField } from "formik"
import { makeStyles } from "tss-react/mui"
import { Grid, Typography, FormControl, InputLabel, Select, MenuItem, Stack, OutlinedInput } from "@mui/material"
import { SelectChangeEvent } from "@mui/material/Select"
import { faShieldCheck } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { SUPPORT_ADMIN_ROUTE, SUPPORT_ADMIN_GDPR_ROUTE } from "../../../../../constants/routes"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { LocalizedStrict, LocalizedEnum } from "../../../../localization/components/AppLocalized"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { FormCard } from "../../../../../shared/components/forms/FormCard"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import { FormikCheckbox } from "../../../../../shared/components/forms/Formik"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { fetchInternalUsers, InternalUsersResponse, InternalUser } from "../gdprApi"
import { GDPRUserType, GDPR_USER_TYPE_TO_API_PARAM } from "../gdprTypes"
import { iterateStringEnum } from "../../../../../lib/enums"
import { useExportContactList } from "../hooks/useExportContactList"

const useStyles = makeStyles()((theme) => ({
    card: {
        marginTop: "2rem",
    },
    formControl: {
        minWidth: "18.75rem",
        marginBottom: "1rem",
    },
    button: {
        marginTop: "1rem",
    },
}))

type FormValues = {
    userTypes: GDPRUserType[]
    includeActiveUsers: boolean
    includeInactiveUsers: boolean
}

function UserTypeSelect() {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const [field, , helpers] = useField<GDPRUserType[]>("userTypes")

    return (
        <FormControl className={classes.formControl}>
            <LocalizedStrict id="support-admin-gdpr-user-type-label">
                <InputLabel id="user-type-select-label">User type</InputLabel>
            </LocalizedStrict>
            <LocalizedStrict id="support-admin-gdpr-user-type-label" attrs={{ label: true }}>
                <Select
                    labelId="user-type-select-label"
                    id="user-type-select"
                    name="userTypes"
                    multiple
                    value={field.value}
                    onChange={(event: SelectChangeEvent<GDPRUserType[]>) => {
                        const {
                            target: { value },
                        } = event
                        const newValue = typeof value === "string" ? (value.split(",") as GDPRUserType[]) : value
                        helpers.setValue(newValue)
                    }}
                    onBlur={field.onBlur}
                    input={<OutlinedInput label="User type" />}
                    renderValue={(selected) =>
                        selected
                            .map((value) =>
                                l10n.getStringForEnum("support-admin-gdpr-user-type-select-item", value, ""),
                            )
                            .filter(Boolean)
                            .join(", ")
                    }
                >
                    {[...iterateStringEnum(GDPRUserType)].map((userType) => (
                        <MenuItem key={userType} value={userType}>
                            <LocalizedEnum base="support-admin-gdpr-user-type-select-item" enumValue={userType}>
                                <span>{userType}</span>
                            </LocalizedEnum>
                        </MenuItem>
                    ))}
                </Select>
            </LocalizedStrict>
        </FormControl>
    )
}

type SupportGDPRFormContentProps = {
    isExporting: boolean
}

function SupportGDPRFormContent({ isExporting }: SupportGDPRFormContentProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const { values, isSubmitting } = useFormikContext<FormValues>()

    return (
        <Grid container direction="column" className={classes.card}>
            <FormCard>
                <LocalizedStrict id="support-admin-gdpr-administrators-title">
                    <Typography variant="subtitle1" sx={{ marginBottom: "1.5rem" }}>
                        Administrators
                    </Typography>
                </LocalizedStrict>

                <Stack spacing={2}>
                    <UserTypeSelect />

                    <LocalizedStrict id="support-admin-gdpr-include-active-users">
                        <FormikCheckbox<FormValues> name="includeActiveUsers">Include active users</FormikCheckbox>
                    </LocalizedStrict>

                    <LocalizedStrict id="support-admin-gdpr-include-inactive-users">
                        <FormikCheckbox<FormValues> name="includeInactiveUsers">Include inactive users</FormikCheckbox>
                    </LocalizedStrict>

                    <LocalizedStrict id="support-admin-gdpr-export-button">
                        <StandardButton
                            type="submit"
                            variant="contained"
                            color="black"
                            disabled={values.userTypes.length === 0 || isExporting || isSubmitting}
                            className={classes.button}
                        >
                            {isExporting || isSubmitting
                                ? l10n.getString("support-admin-gdpr-export-button-exporting")
                                : l10n.getString("support-admin-gdpr-export-button-label")}
                        </StandardButton>
                    </LocalizedStrict>
                </Stack>
            </FormCard>
        </Grid>
    )
}

export function SupportGDPR() {
    const { handleExport: exportToCsv } = useExportContactList()

    const { mutateAsync: fetchUsers, isLoading: isExporting } = useApiMutation(fetchInternalUsers, {
        async onSuccess(params, data) {
            const { originalUserTypes } = params
            if (!originalUserTypes) {
                exportToCsv(data)
                return
            }

            let filteredUsers = data.users || []

            // Filter for COMPANY_AGREEMENT_OWNERS if selected
            // If COMPANY_ADMINS is also selected, we want all COMPANY users (no filtering)
            const needsCompanyAgreementOwnerFilter = originalUserTypes.includes(GDPRUserType.COMPANY_AGREEMENT_OWNERS)
            const needsCompanyAdmins = originalUserTypes.includes(GDPRUserType.COMPANY_ADMINS)

            if (needsCompanyAgreementOwnerFilter && !needsCompanyAdmins) {
                // Only COMPANY_AGREEMENT_OWNERS selected - filter to only agreement owners
                filteredUsers = filteredUsers.filter((user: InternalUser) => user.company?.isAgreementOwner === true)
            }

            // If both are selected, we keep all COMPANY users (no filtering needed)
            const filteredResponse: InternalUsersResponse = {
                users: filteredUsers,
            }

            exportToCsv(filteredResponse)
        },
    })

    const handleSubmit = async (values: FormValues) => {
        if (values.userTypes.length === 0) {
            return
        }

        const userTypeParams: string[] = []
        values.userTypes.forEach((type) => {
            userTypeParams.push(GDPR_USER_TYPE_TO_API_PARAM[type])
        })

        const userIsActiveParams: string[] = []
        userIsActiveParams.push(String(values.includeActiveUsers))
        userIsActiveParams.push(String(values.includeInactiveUsers))

        await fetchUsers({
            userTypes: userTypeParams.join(","),
            userIsActive: userIsActiveParams.join(","),
            originalUserTypes: values.userTypes,
        })
    }

    return (
        <>
            <LocalizedStrict id="support-admin-gdpr-page-header" attrs={{ title: true }}>
                <PageHeader page={Page.SUPPORT_ADMIN_GDPR} title="GDPR" icon={<FontAwesomeIcon icon={faShieldCheck} />}>
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="support-nav-item-home">
                            <BreadcrumbLink to={SUPPORT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="support-admin-gdpr-breadcrumb">
                            <Breadcrumb>GDPR</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <Formik<FormValues>
                initialValues={{
                    userTypes: [],
                    includeActiveUsers: true,
                    includeInactiveUsers: true,
                }}
                onSubmit={handleSubmit}
                validateOnBlur={false}
                validateOnChange={false}
            >
                <Form>
                    <SupportGDPRFormContent isExporting={isExporting} />
                </Form>
            </Formik>
        </>
    )
}

